import { ASPECT_RATIO_TOLERANCE } from './imageUpload.constants';

export function normalizeImageValue(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export function getImgUrl(img) {
  if (!img) return '';
  return typeof img === 'string' ? img : img.url;
}

export function getAspectRatio(aspectRatio) {
  if (!aspectRatio) return {};
  if (
    typeof aspectRatio === 'number' ||
    (typeof aspectRatio === 'string' && !isNaN(Number(aspectRatio)))
  ) {
    return {
      aspectRatioValue: aspectRatio,
      aspectRatioLabel: `${aspectRatio}`,
    };
  }

  if (typeof aspectRatio === 'string' && aspectRatio.includes(':')) {
    const [w, h] = aspectRatio.split(':').map(Number);
    if (w && h) {
      return {
        aspectRatioValue: w / h,
        aspectRatioLabel: `${w}:${h}`,
      };
    }
  }
  return {};
}

export function validateFileToUpload(file, { maxSize, minDimensions, aspectRatioValue }) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const errors = {
        size: maxSize && file.size > maxSize * 1024 * 1024,
        dimensions:
          minDimensions && (img.width < minDimensions.width || img.height < minDimensions.height),
        aspectRatio:
          aspectRatioValue &&
          Math.abs(img.width / img.height - aspectRatioValue) > ASPECT_RATIO_TOLERANCE,
      };

      URL.revokeObjectURL(url);
      resolve({ ...errors, isValid: !Object.values(errors).some(Boolean) });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ isValid: false });
    };

    img.src = url;
  });
}

export function generateUploaderHints({ maxSize, minDimensions, aspectRatioLabel }) {
  const hints = [];

  if (maxSize) hints.push(`Max file size: ${maxSize}MB`);
  if (minDimensions?.width && minDimensions?.height)
    hints.push(`Min dimensions: ${minDimensions.width}x${minDimensions.height}px`);
  if (aspectRatioLabel) hints.push(`Aspect ratio: ${aspectRatioLabel}`);

  return hints.join(', ');
}

export function generateUploaderErrors(
  errors,
  { maxSize, minDimensions, aspectRatioLabel, minImages, maxImages, imageCount },
) {
  let errorTypes = new Set();

  errors.forEach((e) => {
    const { width: minWidth, height: minHeight } = minDimensions || {};
    if (!e.isValid) {
      if (e.size) errorTypes.add(`File size cannot exceed ${maxSize}MB.`);
      if (e.dimensions)
        errorTypes.add(`Image dimensions must be at least ${minWidth}x${minHeight} pixels.`);
      if (e.aspectRatio) errorTypes.add(`Image must have a ${aspectRatioLabel} aspect ratio.`);
    }
  });

  if (typeof minImages === 'number' && imageCount < minImages)
    errorTypes.add(`At least ${minImages} image${minImages > 1 ? 's' : ''} required.`);
  if (typeof maxImages === 'number' && imageCount > maxImages)
    errorTypes.add(`At most ${maxImages} image${maxImages > 1 ? 's' : ''} allowed.`);

  return Array.from(errorTypes).join(', ');
}

export function resolveMinImages({ required, minImages }) {
  if (typeof minImages === 'number') return minImages;
  if (required) return 1;
  return undefined;
}
